"use client";

import * as Popover from "@radix-ui/react-popover";
import { motion } from "framer-motion";
import { useRef, useState, type ReactNode } from "react";

import { POPOVER_MOTION, SURFACE_FEEL } from "../../_logic/motion";

/** Pointer settling time before the card opens, and the grace to cross from the trigger into it. */
const HOVER_DELAY_MS = 150;

interface SourcePopoverProps {
  /** What the planner hovers or taps, e.g. "Venue rates · 110 venues". */
  trigger: ReactNode;
  /** Names the trigger for screen readers, e.g. "Where San Francisco's hotel range comes from". */
  label: string;
  title: string;
  children: ReactNode;
  /** Trigger text classes; quiet gray by default. */
  className?: string;
}

/**
 * Where a number comes from, a hover away: the trigger reads like the caption it replaces
 * (dotted underline), and a small card with the source opens on hover or keyboard focus. On a
 * touch screen a tap opens it; Escape or a press outside closes it.
 * @param props - Trigger, its label, the card title and body.
 * @returns The popover.
 * @throws Never.
 */
export const SourcePopover = ({ trigger, label, title, children, className = "text-gray-500" }: SourcePopoverProps) => {
  // States
  const [open, setOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hovering = useRef(false);

  // Handlers
  const settle = () => {
    if (timer.current) clearTimeout(timer.current);
  };
  const later = (next: boolean) => {
    settle();
    timer.current = setTimeout(() => setOpen(next), HOVER_DELAY_MS);
  };

  // Render
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        {/* ui-lint-disable-next-line raw-control: a custom control (segment, chip, calendar day, stepper, icon or inline editor) styled to its row; FetchButton's sizes and 3D treatment don't fit */}
        <button
          type="button"
          aria-label={label}
          onPointerEnter={(event) => {
            if (event.pointerType !== "mouse") return;
            hovering.current = true;
            later(true);
          }}
          onPointerLeave={(event) => {
            if (event.pointerType !== "mouse") return;
            hovering.current = false;
            later(false);
          }}
          onFocus={(event) => {
            // Keyboard focus only: a tap focuses first on Android, then Radix's toggle would close it.
            if (!event.currentTarget.matches(":focus-visible")) return;
            settle();
            setOpen(true);
          }}
          onBlur={() => later(false)}
          // A mouse click lands on a card the hover already opened; Radix's toggle would close it.
          onClick={(event) => hovering.current && event.preventDefault()}
          className={`rounded text-xs font-medium underline decoration-gray-300 decoration-dotted underline-offset-[3px] transition-colors hover:text-gray-900 hover:decoration-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${className}`}
        >
          {trigger}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={6}
          collisionPadding={12}
          onPointerEnter={settle}
          onPointerLeave={() => later(false)}
          // Hover must not move focus into the card, nor pull it back when the card closes.
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          className="z-50 outline-none"
        >
          <motion.div
            {...POPOVER_MOTION}
            style={{ transformOrigin: "var(--radix-popover-content-transform-origin)" }}
            className={`flex w-80 max-w-[calc(100vw-24px)] flex-col gap-2 rounded-2xl border border-gray-200 bg-white p-4 text-left text-sm font-medium text-gray-700 shadow-xl ${SURFACE_FEEL}`}
          >
            <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
            {children}
          </motion.div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
